import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getTasklists, getTasklistSelectedLocationId } from '../../../../store/tasklists/selectors';
import { useFilter } from '../../../../hooks/useFilter';
import { getSelectedFilterItems } from '../../../../store/common/selectors';
import { TTasklist } from '../../../../types';

export function useFilterTasklists() {
  const tasklists = useAppSelector(getTasklists);
  const selectedFilterItems = useAppSelector(getSelectedFilterItems);
  const tasklistSelectedLocationId = useAppSelector(getTasklistSelectedLocationId);

  const {
    filterByPermissions,
    filterByLocations,
  } = useFilter();
  const { t } = useTranslation('tasklists');

  const filterByCreator = (
    items: Array<TTasklist>,
  ) => {
    const creatorItems = selectedFilterItems.creator;

    return items.filter((item) => creatorItems.some(
      (creator) => creator.value === item.creator.email,
    ));
  };

  const filterByAssign = (
    items: Array<TTasklist>,
  ) => {
    const assignItems = selectedFilterItems?.assign;

    return items.filter((item) => assignItems.some((assign) => assign.value === item.assigneeEmail));
  };

  const tasklistFilterItems = useMemo(() => {
    const uniqueCreators = tasklists.reduce((acc: Array<{ name: string, value: string }>, item) => {
      const { creator: { name, email } } = item;

      if (name && email) {
        const existingCreator = acc.find((entry) => entry.name === name && entry.value === email);
        if (!existingCreator) {
          acc.push({ name, value: email });
        }
      }

      return acc;
    }, []);

    const uniqueAssignee = tasklists.reduce((acc: Array<{ name: string, value: string }>, item) => {
      const { assigneeEmail, assigneeName } = item;

      if (assigneeName && assigneeEmail) {
        const existingCreator = acc.find((entry) => entry.name === assigneeName && entry.value === assigneeEmail);
        if (!existingCreator) {
          acc.push({ name: assigneeName, value: assigneeEmail });
        }
      }

      return acc;
    }, []);

    return [
      {
        value: 'creator',
        selectedKey: 'creator',
        name: t('creator'),
        children: uniqueCreators,
        isSelected: false,
      },
      {
        value: 'assign',
        selectedKey: 'assign',
        name: t('assign'),
        children: uniqueAssignee,
        isSelected: false,
      },
    ];
  }, [tasklists]);

  const getTasklistsByFilters = useMemo(() => {
    let filteredTasklists = tasklists;

    if (selectedFilterItems.role?.length) {
      filteredTasklists = filterByPermissions(filteredTasklists, true);
    }

    if (selectedFilterItems.location?.length) {
      filteredTasklists = filterByLocations(filteredTasklists);
    }

    if (selectedFilterItems.creator?.length) {
      filteredTasklists = filterByCreator(filteredTasklists);
    }

    if (selectedFilterItems.assign?.length) {
      filteredTasklists = filterByAssign(filteredTasklists);
    }

    if (tasklistSelectedLocationId > 0) {
      filteredTasklists = filteredTasklists.filter((member) => member.locations.some((location) => location.id === tasklistSelectedLocationId));
    }

    return filteredTasklists;
  }, [selectedFilterItems, tasklists, tasklistSelectedLocationId]);

  return {
    tasklistFilterItems,
    getTasklistsByFilters,
  };
}
